import { Dices, Lock, LockOpen } from 'lucide-react';
import clsx from 'clsx';
import { BACKGROUND_PARAMS } from '../../core/background';
import { isVisible } from '../../core/params/types';
import { CANVAS_PRESETS, useStudio, type FieldView } from '../../store/studioStore';
import { ParamControl } from '../params/ParamControl';
import { Slider } from '../controls/Slider';
import { Group, Row, Select } from '../controls/Basics';

const FIELD_VIEWS: { value: FieldView; label: string; hint: string }[] = [
  { value: 'render', label: 'Komposition', hint: 'Der fertige Layer-Stapel' },
  { value: 'alpha', label: 'Alpha-Maske', hint: 'Die 8-Bit-Deckungsmaske, aus der das SDF entsteht' },
  { value: 'sdf', label: 'Distanzfeld', hint: 'Vorzeichenbehaftete Distanz mit Isolinien alle 24 px' },
  { value: 'gradient', label: 'Gradient', hint: 'Richtung „weg vom Text“ — lenkt Partikel und Flow-Fields um' },
  { value: 'outline', label: 'Konturen', hint: 'Echte Vektorkonturen inkl. der Punkte aus dem Flattening' },
  { value: 'samples', label: 'Sampling', hint: 'Poisson-Punkte innen, Konturpunkte mit Normalen aussen' },
  { value: 'glyphs', label: 'Glyphen', hint: 'Zerlegung pro Buchstabe: Box, Kontur, Grundlinienpunkt' },
];

export function CanvasPanel(): JSX.Element {
  const canvas = useStudio((s) => s.doc.canvas);
  const background = useStudio((s) => s.doc.background);
  const setBackgroundValue = useStudio((s) => s.setBackgroundValue);
  const seed = useStudio((s) => s.doc.seed);
  const seedLocked = useStudio((s) => s.doc.seedLocked);
  const setCanvas = useStudio((s) => s.setCanvas);
  const setSeed = useStudio((s) => s.setSeed);
  const rollSeed = useStudio((s) => s.rollSeed);
  const toggleSeedLock = useStudio((s) => s.toggleSeedLock);
  const fieldView = useStudio((s) => s.fieldView);
  const setFieldView = useStudio((s) => s.setFieldView);
  const sampleCount = useStudio((s) => s.sampleCount);
  const setSampleCount = useStudio((s) => s.setSampleCount);

  const activePreset =
    CANVAS_PRESETS.find((p) => p.width === canvas.width && p.height === canvas.height)?.id ?? 'custom';

  return (
    <div className="px-3">
      <Group title="Canvas">
        <Row label="Format">
          <Select
            value={activePreset}
            onChange={(id) => {
              const preset = CANVAS_PRESETS.find((p) => p.id === id);
              if (preset) setCanvas({ width: preset.width, height: preset.height });
            }}
            options={[
              ...CANVAS_PRESETS.map((p) => ({
                value: p.id,
                label: `${p.label} — ${p.width}×${p.height}`,
                group: p.note ?? 'Bildschirm',
              })),
              { value: 'custom', label: 'Frei', group: 'Frei' },
            ]}
          />
        </Row>
        <Slider
          label="Breite"
          value={canvas.width}
          onChange={(width) => setCanvas({ width: Math.round(width) })}
          min={128}
          max={5000}
          step={1}
          unit="px"
          precision={0}
        />
        <Slider
          label="Höhe"
          value={canvas.height}
          onChange={(height) => setCanvas({ height: Math.round(height) })}
          min={128}
          max={5000}
          step={1}
          unit="px"
          precision={0}
        />
        <Slider
          label="Export-Skalierung"
          value={canvas.exportScale}
          onChange={(exportScale) => setCanvas({ exportScale: Math.round(exportScale) })}
          min={1}
          max={4}
          step={1}
          defaultValue={2}
          unit="×"
          precision={0}
          tooltip="Supersampling beim Export. Die Vorschau rendert immer in 1×."
        />
      </Group>

      <Group title="Hintergrund">
        {BACKGROUND_PARAMS.filter((p) => isVisible(p, background)).map((param) => (
          <ParamControl
            key={param.key}
            param={param}
            value={background[param.key]}
            bulk={undefined}
            onChange={(v) => setBackgroundValue(param.key, v)}
            onBulkChange={() => undefined}
            onToggleLock={() => undefined}
            onRandomize={() => undefined}
          />
        ))}
      </Group>

      <Group title="Seed">
        <div className="flex items-center gap-1.5 py-1">
          <input
            value={seed}
            onChange={(e) => {
              const v = Number.parseInt(e.target.value.replace(/\D/g, ''), 10);
              setSeed(Number.isFinite(v) ? v : 0);
            }}
            className="field-input font-mono text-[11px]"
            spellCheck={false}
          />
          <button
            type="button"
            className="ghost-button h-7 w-7 shrink-0 p-0"
            title={seedLocked ? 'Seed ist gesperrt' : 'Neuen Seed würfeln'}
            onClick={rollSeed}
            disabled={seedLocked}
          >
            <Dices size={13} />
          </button>
          <button
            type="button"
            className={clsx('ghost-button h-7 w-7 shrink-0 p-0', seedLocked && 'text-accent-500')}
            title={seedLocked ? 'Seed entsperren' : 'Seed sperren'}
            onClick={toggleSeedLock}
          >
            {seedLocked ? <Lock size={13} /> : <LockOpen size={13} />}
          </button>
        </div>
        <p className="pb-1 text-[10px] leading-relaxed text-ink-500">
          Jede Zufälligkeit läuft über einen deterministischen Generator. Gleicher Seed plus gleiche Parameter
          ergeben exakt dasselbe Bild.
        </p>
      </Group>

      <Group title="Feld-Inspektor" defaultOpen={false}>
        <div className="grid grid-cols-2 gap-1 py-1">
          {FIELD_VIEWS.map((v) => (
            <button
              key={v.value}
              type="button"
              title={v.hint}
              onClick={() => setFieldView(v.value)}
              className={clsx(
                'rounded-md px-2 py-1.5 text-left text-[11px] transition-colors',
                fieldView === v.value ? 'bg-ink-700 text-ink-100' : 'bg-ink-850 text-ink-400 hover:text-ink-200',
              )}
            >
              {v.label}
            </button>
          ))}
        </div>
        <p className="pb-1 pt-1 text-[10px] leading-relaxed text-ink-500">
          {FIELD_VIEWS.find((v) => v.value === fieldView)?.hint}
        </p>
        {fieldView === 'samples' && (
          <Slider
            label="Sample-Anzahl"
            value={sampleCount}
            onChange={(n) => setSampleCount(Math.round(n))}
            min={50}
            max={20000}
            step={1}
            scale="log"
            defaultValue={900}
            precision={0}
          />
        )}
      </Group>
    </div>
  );
}
